import { useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PreviewIcon from '@mui/icons-material/Preview';

interface PromptPreviewPanelProps {
  content: string;
  contextToggles: Record<string, boolean>;
}

const CONTEXT_SECTIONS = [
  { key: 'include_user_context', label: 'User Context', example: '## User Context\n- Name: Jane Smith\n- Role: Sales Manager' },
  { key: 'include_org_context', label: 'Org Context', example: '## Organization Context\n- Organization: Acme Corp' },
  { key: 'include_prospect_context', label: 'Prospect Context', example: '## Prospect Context\n- Name: John Doe\n- Company: WidgetCo\n- Title: VP Sales' },
  { key: 'include_transcript_context', label: 'Transcript Context', example: '## Transcript\n[Call transcript content...]' },
];

function estimateTokens(text: string): number {
  // Rough approximation: ~4 chars per token for English text
  return Math.ceil(text.length / 4);
}

export function PromptPreviewPanel({
  content,
  contextToggles,
}: PromptPreviewPanelProps) {
  const [expanded, setExpanded] = useState(false);

  const enabledSections = useMemo(
    () => CONTEXT_SECTIONS.filter((s) => contextToggles[s.key] !== false),
    [contextToggles]
  );

  const composedPreview = useMemo(() => {
    let preview = content || '(empty prompt)';
    if (enabledSections.length > 0) {
      preview += '\n\n---\n\n' + enabledSections.map((s) => s.example).join('\n\n');
    }
    return preview;
  }, [content, enabledSections]);

  const tokenEstimate = estimateTokens(composedPreview);

  return (
    <Accordion expanded={expanded} onChange={(_, v) => setExpanded(v)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PreviewIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Prompt Preview</Typography>
          <Chip label={`~${tokenEstimate} tokens`} size="small" variant="outlined" />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            mb: 1,
            display: 'block'
          }}>
          Preview of the composed prompt with enabled context sections (using sample data).
        </Typography>
        <Box
          sx={{
            bgcolor: 'action.hover',
            borderRadius: 1,
            p: 2,
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            whiteSpace: 'pre-wrap',
            maxHeight: 400,
            overflow: 'auto',
          }}
        >
          {composedPreview}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
